import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, finalize, Observable, of, tap } from 'rxjs';
import {
  Institution,
  School,
  SchoolTermGrade,
  SchoolGradeYearDto,
  SchoolContact,
  StudentEnrollment,
  UpdateSchoolModel
} from '../models/school.model';
import { ApiResponse, Person, SchoolTerm, ToastMessageType } from '@k12/models';
import { HttpClient } from '@angular/common/http';
import { SystemService } from './system.service';
import { EnvironmentService } from './environment.service';
import { environment } from '../../environments/environment';
import { ToastMessageService, LoadingSpinnerService } from '@k12/app-services';

// Response types for API calls
interface GetInstitutionsResponse {
  success: boolean;
  entity: {
    institutions: Institution[];
  };
  message?: string;
}

interface SchoolContactResponse {
  success: boolean;
  entity: SchoolContact;
  message?: string;
}

interface SchoolContactsResponse {
  success: boolean;
  entity: SchoolContact[];
  message?: string;
}

interface SchoolTermGradesResponse {
  success: boolean;
  entity: SchoolTermGrade[];
  message?: string;
}

interface SchoolTermsResponse {
  success: boolean;
  entity: SchoolTerm[];
  message?: string;
}

interface AttendanceType {
  attendanceTypeId: number;
  name: string;
  description?: string;
}

interface AttendanceTypesResponse {
  success: boolean;
  entity: AttendanceType[];
  message?: string;
}

interface EnrollmentResponse {
  success: boolean;
  entity: StudentEnrollment;
  message?: string;
}

interface UpdateTermsResponse {
  success: boolean;
  entity: SchoolTermGrade[];
  message?: string;
}

interface HdmaContact extends Person {
  isHDMA: boolean;
}

interface HdmaContactsResponse {
  success: boolean;
  entity: HdmaContact[];
  message?: string;
}

interface DeleteResponse {
  success: boolean;
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class SchoolsService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly systemService: SystemService = inject(SystemService);
  private readonly environmentService: EnvironmentService = inject(EnvironmentService);
  private readonly toastMessageService: ToastMessageService = inject(ToastMessageService);
  private readonly loadingService: LoadingSpinnerService = inject(LoadingSpinnerService);

  schools: BehaviorSubject<School[]> = new BehaviorSubject<School[]>([]);
  private readonly currentSchool$: BehaviorSubject<School> = new BehaviorSubject<School>({
    programPartnerId: '',
    institutionId: '',
    recordNumber: '',
    name: '',
    hdmaAccountId: '',
    schoolTypeId: 0,
    schoolType: '',
    operationalScheduleTypeId: 0,
    operationalScheduleType: '',
    instructionTypeIds: [],
    schoolInstructionType: '',
    programTypeId: 0,
    programType: '',
    isRemote: false,
    backgroundCheckValidatedDate: '2024-03-15T00:00:00',
    backgroundCheckNameOnFile: 'Jane Director (Mock)',
    tuitionFeesUploadedDate: '2024-05-30T00:00:00',
    areTuitionFeesApproved: true,
    dnpeNumber: '',
    associatedProviderRecordNumber: '',
    providerAccessInitiatedDate: '2024-04-30T00:00:00',
    address1: '',
    address2: '',
    city: '',
    county: '',
    state: '',
    zip: '',
    studentCount: 0,
    hasNPSAgreement: false
  });

  institutions: BehaviorSubject<Institution[]> = new BehaviorSubject<Institution[]>([]);
  private readonly currentInstitution$: BehaviorSubject<Institution | null> = new BehaviorSubject<Institution | null>(
    null
  );

  private currentSchools: School[] = [];
  private currentInstitutions: Institution[] = [];

  init(): void {
    // idempotent guard
    if (this.currentSchools.length === 0) {
      this.loadSchools();
    }
  }

  public loadSchools() {
    this.loadingService.showSpinner();
    this.http
      .get<GetInstitutionsResponse>(`${environment.adminApiUrl}/schools/GetInstitutions`)
      .pipe(
        tap((res) => {
          const schools: School[] = res.entity.institutions.flatMap((institution: Institution) =>
            institution.institutionSchools.map((school: School) => school)
          );

          const institutions: Institution[] = res.entity.institutions.map((inst: Institution) => ({
            ...inst,
            id: inst.id ?? inst.institutionId
          }));
          this.currentInstitutions = institutions;
          this.institutions.next(institutions);

          this.currentSchools = schools;
          this.schools.next(this.currentSchools);

          if (this.currentSchools.length > 0) {
            this.currentSchool$.next(this.currentSchools[0]);
            this.setCurrentInstitution(this.currentSchools[0].institutionId);
          } else {
            console.warn('No institutions returned from API');
          }

          //this.toastMessageService.showToast(ToastMessageType.Success, 'Institutions loaded successfully');
        }),
        catchError(() => {
          // Populate local fallbacks for both schools and institutions
          this.toastMessageService.showToast(ToastMessageType.Error, 'Failed to load School(s)');
          return of(null);
        }),
        finalize(() => {
          this.loadingService.hideSpinner();
        })
      )
      .subscribe({
        error: (err) => console.error('Error in loadSchools subscription:', err)
      });
  }

  public getCurrentSchool(): Observable<School> {
    return this.currentSchool$;
  }

  public getCurrentInstitution(): Observable<Institution | null> {
    return this.currentInstitution$;
  }

  getSchoolHdmaContacts(id: string): Observable<HdmaContactsResponse> {
    return this.http.get<HdmaContactsResponse>(environment.adminApiUrl + `/schools/${id}/hdmacontacts`);
  }

  getSchoolTermGrades(programPartnerId: string, termDate: Date = new Date()): Observable<SchoolTermGradesResponse> {
    const iso = termDate.toISOString();
    const url = `${environment.adminApiUrl}/schools/${programPartnerId}/grades/${encodeURIComponent(iso)}`;
    return this.http.get<SchoolTermGradesResponse>(url);
  }

  getSchoolTerms(programPartnerId: string): Observable<SchoolTermsResponse> {
    const url = `${environment.adminApiUrl}/schools/${programPartnerId}/terms`;
    return this.http.get<SchoolTermsResponse>(url);
  }

  getSchoolGradeYears(programPartnerId: string, academicYearId: number): Observable<SchoolGradeYearDto[]> {
    const url = `${environment.adminApiUrl}/schools/${programPartnerId}/grade-years/${academicYearId}`;
    return this.http.get<SchoolGradeYearDto[]>(url);
  }

  updateSchoolGradeYears(programPartnerId: string, gradeYears: SchoolGradeYearDto[]): Observable<void> {
    const url = `${environment.adminApiUrl}/schools/${programPartnerId}/grade-years`;
    return this.http.put<void>(url, gradeYears);
  }

  getAttendanceTypes(): Observable<AttendanceTypesResponse> {
    const url = `${environment.adminApiUrl}/schools/attendance-types`;
    return this.http.get<AttendanceTypesResponse>(url);
  }

  updateStudentEnrollment(studentEnrollmentDto: StudentEnrollment): Observable<EnrollmentResponse> {
    const url = `${environment.adminApiUrl}/schools/enrollments`;
    return this.http.put<EnrollmentResponse>(url, studentEnrollmentDto);
  }

  updateSchoolTerms(schoolTerms: SchoolTermGrade[]): Observable<UpdateTermsResponse> {
    const url = `${environment.adminApiUrl}/schools/termgrades`;
    return this.http.put<UpdateTermsResponse>(url, schoolTerms);
  }

  setCurrentInstitution(institutionId: string): void {
    const found = this.currentInstitutions.find((i) => i.id === institutionId);
    if (!found) {
      this.toastMessageService.showToast(ToastMessageType.Error, 'Institution not found');
      return;
    }
    this.currentInstitution$.next(found);
  }

  setCurrentSchool(programPartnerId: string): void {
    const foundSchool = this.currentSchools.find((school) => school.programPartnerId === programPartnerId);
    if (!foundSchool) {
      this.toastMessageService.showToast(ToastMessageType.Error, 'School not found');
      return;
    }
    this.currentSchool$.next(foundSchool);
  }

  private setCurrentSchoolInternal(foundSchool: School): void {
    this.currentSchool$.next(foundSchool);
  }

  updateSchool(
    programPartnerId: string,
    updateSchoolModel: UpdateSchoolModel
  ): Observable<{ success: boolean; entity?: School; message?: string }> {
    const url = `${environment.adminApiUrl}/schools/${programPartnerId}`;
    const result = this.http.put<{ success: boolean; entity?: School; message?: string }>(url, updateSchoolModel);
    // update the entity in the cache
    result.subscribe({
      next: (response) => {
        if (response?.entity) {
          const cachedValue = this.currentSchools.find((s) => s.programPartnerId === programPartnerId);
          if (cachedValue) {
            const updatedSchool = { ...cachedValue, ...response.entity } as School;
            this.setCurrentSchoolInternal(updatedSchool);
          }
        }
      },
      error: (err) => console.error('Error updating school cache:', err)
    });
    return result;
  }

  // Get all contacts for a school
  getSchoolContacts(programPartnerId: string): Observable<SchoolContactsResponse> {
    return this.http.get<SchoolContactsResponse>(`${environment.adminApiUrl}/schools/${programPartnerId}/contacts`);
  }

  // Get a specific contact for a school
  getSchoolContact(programPartnerId: string, personId: string): Observable<SchoolContactResponse> {
    return this.http.get<SchoolContactResponse>(
      `${environment.adminApiUrl}/schools/${programPartnerId}/contacts/${personId}`
    );
  }

  // Create a new school contact
  createSchoolContact(contact: SchoolContact): Observable<SchoolContactResponse> {
    return this.http.post<SchoolContactResponse>(`${environment.adminApiUrl}/schools/contacts`, contact);
  }

  // Update an existing school contact
  updateSchoolContact(contact: SchoolContact): Observable<SchoolContactResponse> {
    return this.http.put<SchoolContactResponse>(`${environment.adminApiUrl}/schools/contacts`, contact);
  }

  // Delete a school contact
  deleteSchoolContact(programPartnerId: string, personId: string): Observable<DeleteResponse> {
    return this.http.delete<DeleteResponse>(
      `${environment.adminApiUrl}/schools/${programPartnerId}/contacts/${personId}`
    );
  }

  initProviderAccess(programPartnerId: string): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(
      `${environment.adminApiUrl}/schools/${programPartnerId}/init-provider-access`,
      {}
    );
  }

  // Promote a contact to HDMA
  promoteContactToHDMA(programPartnerId: string, personId: string): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(
      `${environment.adminApiUrl}/schools/${programPartnerId}/contacts/${personId}/promote-hdma`,
      {}
    );
  }
}
